"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { FiChevronDown, FiFilter, FiGrid, FiX } from "react-icons/fi";
import {
  MdOutlineBlender,
  MdOutlineCoffeeMaker,
  MdOutlineKitchen,
  MdOutlineMicrowave,
  MdOutlineRiceBowl,
} from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import NoData from "../components/NoData";
import SingleProduct from "../components/product/SingleProduct";
import { getCategory } from "../redux/features/CategorySlice";
import { getProducts } from "../redux/features/ProductSlice";

const SubCategoryProduct = () => {
  const { main, sub } = useParams();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(sub || "");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const filterRef = useRef(null);

  const categories = useSelector((state) => state.maincategory.categories);
  const subcategories = useSelector(
    (state) => state.maincategory.subcategories
  );
  const products = useSelector((state) => state.mainproduct.products);

  const mainCategory = categories?.find((cat) => cat?.slug === main);
  const subCategory = categories?.find((cat) => cat?.slug === sub);

  const tabData = subcategories?.map((cat) => ({
    label: cat?.name,
    value: cat?.slug,
    id: cat?.id,
  }));

  useEffect(() => {
    const filter = { "parent.slug": { $eq: main } };
    dispatch(getCategory({ filter }));
  }, [dispatch, main]);

  useEffect(() => {
    if (activeTab !== "") {
      const id = tabData?.find((data) => data?.value === activeTab)?.value;
      if (id) {
        dispatch(getProducts({ limit: 20, category_id: id }));
      }
    } else if (main) {
      dispatch(getProducts({ limit: 20, main_category_id: main }));
    }
  }, [dispatch, activeTab, main, tabData]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getCategoryIcon = (name) => {
    switch (name?.toLowerCase()) {
      case "coffee makers":
        return <MdOutlineCoffeeMaker size={24} />;
      case "blenders":
        return <MdOutlineBlender size={24} />;
      case "microwaves":
        return <MdOutlineMicrowave size={24} />;
      case "kitchen appliances":
        return <MdOutlineKitchen size={24} />;
      case "food processors":
        return <MdOutlineRiceBowl size={24} />;
      default:
        return <FiGrid size={24} />;
    }
  };

  const toggleFilter = (filter) => {
    setSelectedFilters((prev) =>
      prev.includes(filter)
        ? prev.filter((f) => f !== filter)
        : [...prev, filter]
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Helmet>
        <title>
          {`${main.charAt(0).toUpperCase() + main.slice(1)}'s ${
            activeTab !== ""
              ? tabData?.find((d) => d?.value === activeTab)?.label
              : "All Products"
          } | KitchenPro`}
        </title>
      </Helmet>

      <motion.div
        className="relative text-white bg-gradient-to-r from-teal-500 to-blue-500"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="container px-4 py-12 mx-auto">
          <h1 className="mb-2 text-4xl font-bold">
            {subCategory?.title ||
              `${main.charAt(0).toUpperCase() + main.slice(1)}'s ${
                activeTab !== ""
                  ? tabData?.find((d) => d?.value === activeTab)?.label
                  : "All Products"
              }`}
          </h1>
          {subCategory?.description && (
            <p className="text-lg opacity-90">{subCategory.description}</p>
          )}
        </div>
        <div className="absolute bottom-0 left-0 w-full h-16 bg-gradient-to-t from-gray-100 to-transparent"></div>
      </motion.div>

      <div className="container px-4 py-8 mx-auto">
        <div className="flex flex-wrap items-center justify-between mb-8">
          <motion.div
            className="flex flex-wrap gap-3 mb-4 md:mb-0"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <motion.button
              onClick={() => setActiveTab("")}
              className={`flex items-center px-4 py-2 text-sm font-medium rounded-full transition-all ${
                activeTab === ""
                  ? "bg-teal-500 text-white shadow-lg"
                  : "bg-white text-gray-700 hover:bg-gray-100"
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiGrid className="mr-2" />
              All Products
            </motion.button>
            {tabData?.map((tab) => (
              <motion.button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={`flex items-center px-4 py-2 text-sm font-medium rounded-full transition-all ${
                  activeTab === tab.value
                    ? "bg-teal-500 text-white shadow-lg"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="mr-2">{getCategoryIcon(tab.label)}</span>
                <span>{tab.label}</span>
              </motion.button>
            ))}
          </motion.div>
          <motion.button
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-teal-500 rounded-full shadow-md hover:bg-teal-600"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FiFilter className="mr-2" />
            Filters
            <FiChevronDown
              className={`ml-2 transition-transform ${
                isFilterOpen ? "rotate-180" : ""
              }`}
            />
          </motion.button>
        </div>

        <AnimatePresence>
          {isFilterOpen && (
            <motion.div
              ref={filterRef}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="p-6 mb-8 bg-white rounded-lg shadow-lg"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Filters</h3>
                <button
                  onClick={() => setIsFilterOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FiX size={20} />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                {["Brand", "Price", "Color", "Features"].map((filter) => (
                  <motion.button
                    key={filter}
                    onClick={() => toggleFilter(filter)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedFilters.includes(filter)
                        ? "bg-teal-500 text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {filter}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <AnimatePresence>
            {products?.length ? (
              products?.map((prod, index) => (
                <motion.div
                  key={prod.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                >
                  <SingleProduct product={prod} />
                </motion.div>
              ))
            ) : (
              <motion.div
                className="col-span-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <NoData />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

export default SubCategoryProduct;
